import React, { useState, useEffect } from 'react';
import { getData, postData, deleteData } from '../../services/api.service';
import { toastSuccess, toastError, toastWarn } from '../common/Toast';
import { useAuth } from '../../context/AuthContext';

function StatusChangeModal({ isOpen, onClose, order, onStatusChanged }) {
  const { user } = useAuth(); // Obtener usuario autenticado
  
  // Estados básicos
  const [statuses, setStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Estado financiero
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [activityHistory, setActivityHistory] = useState([]);
  const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(false);
  const [activeHistoryTab, setActiveHistoryTab] = useState('payments'); // 'payments' o 'activity'
  
  // Formulario de pago
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [creditedAmount, setCreditedAmount] = useState('');
  const [paymentObservations, setPaymentObservations] = useState('');
  
  // Formulario de cambio de estado
  const [controlled, setControlled] = useState(false);
  const [activityDescription, setActivityDescription] = useState('');
  
  // Modal de confirmación para eliminar pago
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  
  // Validación
  const [validation, setValidation] = useState(null);

  useEffect(() => {
    if (isOpen && order) {
      loadStatuses();
      loadPaymentStatus();
      loadPaymentHistory();
      loadActivityHistory();
    }
  }, [isOpen, order]);

  useEffect(() => {
    if (selectedStatus && order) {
      validateStateChange();
    }
  }, [selectedStatus, order]);

  const loadStatuses = async () => {
    try {
      // Agregar timestamp para evitar caché
      const timestamp = new Date().getTime();
      const response = await getData(`/api/orders/statuses?_t=${timestamp}`, false);
      
      if (response?.data?.success && response.data.data) {
        if (Array.isArray(response.data.data)) {
          setStatuses(response.data.data);
        } else {
          setStatuses([]);
        }
      } else {
        setStatuses([]);
      }
    } catch (error) {
      console.error('Error cargando estados:', error);
      setStatuses([]);
    }
  };

  const loadPaymentStatus = async () => {
    if (!order?.order_id) return;
    
    setLoadingPaymentInfo(true);
    try {
      const response = await getData(`/api/orders/payment-status?order_id=${order.order_id}`, false);
      
      if (response?.data?.success) {
        setPaymentStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando estado de pago:', error);
    } finally {
      setLoadingPaymentInfo(false);
    }
  };

  const loadPaymentHistory = async () => {
    if (!order?.order_id) return;
    
    try {
      const response = await getData(`/api/orders/payment-history?order_id=${order.order_id}`, false);
      
      if (response?.data?.success) {
        setPaymentHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('Error cargando historial de pagos:', error);
    }
  };

  const loadActivityHistory = async () => {
    if (!order?.order_id) return;
    
    try {
      const response = await getData(`/api/orders/activity-history?order_id=${order.order_id}`, false);
      
      if (response?.data?.success) {
        setActivityHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('Error cargando historial de actividad:', error);
    }
  };

  const handleUndoLastPayment = async () => {
    if (!order?.order_id || paymentHistory.length === 0) return;
    
    // Obtener el último pago para mostrar en el modal
    const lastPayment = paymentHistory[0];
    setPaymentToDelete(lastPayment);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeletePayment = async () => {
    if (!order?.order_id) return;
    
    setShowDeleteConfirmModal(false);
    setLoading(true);
    
    try {
      const response = await deleteData(`/api/orders/${order.order_id}/undo-last-payment`);
      
      if (response?.data?.success) {
        toastSuccess(`Pago eliminado exitosamente`);
        // Recargar datos
        await Promise.all([
          loadPaymentStatus(),
          loadPaymentHistory(),
          loadActivityHistory()
        ]);
        
        // Notificar al componente padre si existe callback
        if (onStatusChanged) {
          onStatusChanged({
            ...order,
            credited_payment: response.data.data.newTotals.credited_payment,
            credit_balance: response.data.data.newTotals.credit_balance
          });
        }
      }
    } catch (error) {
      console.error('Error deshaciendo pago:', error);
      if (error.response?.data?.msg) {
        toastError(error.response.data.msg);
      } else {
        toastError('Error al eliminar el pago');
      }
    } finally {
      setLoading(false);
      setPaymentToDelete(null);
    }
  };

  const validateStateChange = async () => {
    if (!selectedStatus || !order?.order_id) return;
    
    try {
      const response = await postData('/api/orders/validate-payment-state-change', {
        order_id: order.order_id,
        new_status_id: parseInt(selectedStatus)
      }, false);
      
      if (response?.data?.success) {
        setValidation(response.data.data);
      }
    } catch (error) {
      console.error('Error validando cambio de estado:', error);
    }
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    
    if (!paymentAmount && !creditedAmount) {
      toastWarn('Debe ingresar al menos un monto');
      return;
    }

    setLoading(true);
    try {
      const response = await postData('/api/orders/register-payment', {
        order_id: order.order_id,
        payment_amount: parseFloat(paymentAmount) || 0,
        credited_amount: parseFloat(creditedAmount) || 0,
        observations: paymentObservations,
        updated_by: user?.id || user?.user_id || 1
      });

      if (response?.data?.success) {
        toastSuccess('Pago registrado exitosamente');
        setPaymentAmount('');
        setCreditedAmount('');
        setPaymentObservations('');
        setShowPaymentForm(false);
        
        // Recargar información
        await loadPaymentStatus();
        await loadPaymentHistory();
        await loadActivityHistory();
        
        // Si había validación, revalidar
        if (selectedStatus) {
          await validateStateChange();
        }
      } else {
        toastError(response?.data?.message || 'Error al registrar el pago');
      }
    } catch (error) {
      console.error('Error registrando pago:', error);
      toastError('Error al registrar el pago');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (e) => {
    e.preventDefault();
    
    if (!selectedStatus) {
      toastWarn('Debe seleccionar un estado');
      return;
    }

    // Verificar validación de pago
    if (validation && !validation.allowed) {
      toastError(validation.message);
      return;
    }

    const selectedStatusInfo = statuses.find(s => s.id === parseInt(selectedStatus));
    
    if (selectedStatusInfo?.require_control === 1 && !controlled) {
      toastWarn(`El estado "${selectedStatusInfo.status_description}" requiere marcar como controlado`);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        order_id: order.order_id,
        new_status: parseInt(selectedStatus),
        updated_by: user?.id || user?.user_id || 1,
        controlled: controlled,
        activity_description: activityDescription || `Cambio de estado a ${selectedStatusInfo?.status_description}`,
        // Incluir valores actuales de pago para que no se pierdan en el backend
        credited_payment: paymentStatus?.credited_payment || 0,
        credit_balance: paymentStatus?.credit_balance || 0
      };

      const response = await postData('/api/orders/change-status', payload);

      if (response?.data?.success) {
        toastSuccess('Estado cambiado exitosamente');
        onStatusChanged(response.data.order);
        handleClose();
      } else {
        toastError(response?.data?.message || 'Error al cambiar el estado');
      }
    } catch (error) {
      console.error('Error cambiando estado:', error);
      toastError('Error al cambiar el estado del pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStatus('');
    setControlled(false);
    setActivityDescription('');
    setPaymentAmount('');
    setCreditedAmount('');
    setPaymentObservations('');
    setShowPaymentForm(false);
    setValidation(null);
    onClose();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  const selectedStatusInfo = statuses.find(s => s.id === parseInt(selectedStatus));
  const requiresPaymentValidation = selectedStatusInfo?.payment_requirement_type !== 'NONE';

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-arrow-repeat me-2"></i>
              Gestión de Estado - Pedido #{order?.order_id}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={handleClose}></button>
          </div>

          <div className="modal-body">
            <div className="row">
              {/* COLUMNA IZQUIERDA: Información Financiera */}
              <div className="col-md-5">
                <div className="card mb-3">
                  <div className="card-header bg-info text-white">
                    <h6 className="mb-0">
                      <i className="bi bi-cash-stack me-2"></i>
                      Estado Financiero
                    </h6>
                  </div>
                  <div className="card-body">
                    {loadingPaymentInfo ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary"></div>
                        <p className="mt-2 mb-0">Cargando...</p>
                      </div>
                    ) : paymentStatus ? (
                      <>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="fw-bold">Total del Pedido:</span>
                            <span className="fs-5 text-primary">{formatCurrency(paymentStatus.order_total)}</span>
                          </div>
                          
                          {/* Mostrar interés si existe */}
                          {paymentStatus.interest_rate > 0 && (
                            <div className="mt-2 p-2 bg-light rounded border">
                              <small className="text-muted d-block">
                                + Interés ({paymentStatus.interest_rate}%): {formatCurrency(paymentStatus.interest_amount)}
                              </small>
                              <div className="d-flex justify-content-between align-items-center mt-1">
                                <strong className="text-success">Total a Cobrar:</strong>
                                <strong className="fs-5 text-success">{formatCurrency(paymentStatus.total_with_interest)}</strong>
                              </div>
                              <small className="text-muted d-block mt-1">
                                {paymentStatus.payment_installments} cuota{paymentStatus.payment_installments > 1 ? 's' : ''} de {formatCurrency(paymentStatus.installment_amount)}
                              </small>
                            </div>
                          )}
                          
                          <hr className="my-2" />
                          
                          <div className="mb-2">
                            <small className="text-muted">Forma de Pago:</small>
                            <div className="fw-bold">{paymentStatus.payment_form_description || 'No especificada'}</div>
                            {paymentStatus.requires_installments && (
                              <small className="text-muted">
                                Cuotas: {paymentStatus.payment_installments || 1}
                              </small>
                            )}
                          </div>

                          <div className="mb-2">
                            <small className="text-muted">Método de Pago:</small>
                            <div>{paymentStatus.payment_method_description || 'No especificado'}</div>
                          </div>
                        </div>

                        <div className="mb-3">
                          {/* Fila superior: 2 columnas con más espacio */}
                          <div className="row g-3 mb-3">
                            {/* Columna izquierda: Pagado por cliente */}
                            <div className="col-md-6 col-12">
                              <div className="text-center p-3 bg-success bg-opacity-10 rounded border border-success border-2 h-100 d-flex flex-column justify-content-between">
                                <i className="bi bi-person-check-fill text-success fs-4 mb-2"></i>
                                <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>Pagado por cliente</div>
                                <div 
                                  className="text-success fw-bold mb-2 w-100 px-1" 
                                  style={{ 
                                    fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                                    lineHeight: '1.3',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    hyphens: 'auto'
                                  }}
                                >
                                  {formatCurrency(paymentStatus.credit_balance)}
                                </div>
                                <div className="progress mb-2 w-100" style={{ height: '6px' }}>
                                  <div 
                                    className="progress-bar bg-success" 
                                    style={{ width: `${paymentStatus.client_payment_percentage}%` }}
                                  ></div>
                                </div>
                                <small className="text-muted fw-semibold" style={{ fontSize: '0.7rem' }}>
                                  {paymentStatus.client_payment_percentage}% del total
                                </small>
                              </div>
                            </div>

                            {/* Columna derecha: Acreditado en caja */}
                            <div className="col-md-6 col-12">
                              <div className="text-center p-3 bg-info bg-opacity-10 rounded border border-info border-2 h-100 d-flex flex-column justify-content-between">
                                <i className="bi bi-bank2 text-info fs-4 mb-2"></i>
                                <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>Acreditado en caja</div>
                                <div 
                                  className="text-info fw-bold mb-2 w-100 px-1" 
                                  style={{ 
                                    fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                                    lineHeight: '1.3',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    hyphens: 'auto'
                                  }}
                                >
                                  {formatCurrency(paymentStatus.credited_payment)}
                                </div>
                                <div className="progress mb-2 w-100" style={{ height: '6px' }}>
                                  <div 
                                    className="progress-bar bg-info" 
                                    style={{ width: `${paymentStatus.credited_percentage}%` }}
                                  ></div>
                                </div>
                                <small className="text-muted fw-semibold" style={{ fontSize: '0.7rem' }}>
                                  {paymentStatus.credited_percentage}% del total
                                </small>
                              </div>
                            </div>
                          </div>

                          {/* Fila inferior: Pendiente centrado y destacado */}
                          <div className={`text-center p-3 rounded border border-2 ${paymentStatus.pending_payment > 0 ? 'bg-danger bg-opacity-10 border-danger' : 'bg-success bg-opacity-10 border-success'}`}>
                            <i className={`bi ${paymentStatus.pending_payment > 0 ? 'bi-hourglass-split text-danger' : 'bi-check-circle-fill text-success'} fs-3 mb-2`}></i>
                            <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>Pendiente de pago</div>
                            <div 
                              className={`fw-bold w-100 px-2 ${paymentStatus.pending_payment > 0 ? 'text-danger' : 'text-success'}`}
                              style={{ 
                                fontSize: 'clamp(1.2rem, 4vw, 1.75rem)',
                                lineHeight: '1.2',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                hyphens: 'auto'
                              }}
                            >
                              {formatCurrency(paymentStatus.pending_payment)}
                            </div>
                          </div>
                        </div>

                        {/* Botón para mostrar formulario de pago */}
                        <button
                          type="button"
                          className="btn btn-outline-success w-100"
                          onClick={() => setShowPaymentForm(!showPaymentForm)}
                        >
                          <i className="bi bi-plus-circle me-2"></i>
                          {showPaymentForm ? 'Cancelar' : 'Registrar Nuevo Pago'}
                        </button>

                        {/* Formulario de registro de pago */}
                        {showPaymentForm && (
                          <form onSubmit={handleRegisterPayment} className="mt-3 p-3 border rounded bg-light">
                            <h6 className="mb-3">Nuevo Pago</h6>
                            
                            <div className="mb-3">
                              <label className="form-label">
                                <small>Monto que paga el cliente ahora:</small>
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                placeholder="0.00"
                              />
                              <small className="text-muted">
                                Para TC/TD puede diferir del monto acreditado
                              </small>
                            </div>

                            <div className="mb-3">
                              <label className="form-label">
                                <small>Monto acreditado en caja:</small>
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={creditedAmount}
                                onChange={(e) => setCreditedAmount(e.target.value)}
                                placeholder="0.00"
                              />
                              <small className="text-muted">
                                Lo que efectivamente ingresa a caja
                              </small>
                            </div>

                            <div className="mb-3">
                              <label className="form-label">
                                <small>Observaciones:</small>
                              </label>
                              <textarea
                                className="form-control"
                                rows="2"
                                value={paymentObservations}
                                onChange={(e) => setPaymentObservations(e.target.value)}
                                placeholder="Detalles del pago..."
                              />
                            </div>

                            <button
                              type="submit"
                              className="btn btn-success w-100"
                              disabled={loading}
                            >
                              {loading ? 'Registrando...' : 'Registrar Pago'}
                            </button>
                          </form>
                        )}
                      </>
                    ) : (
                      <div className="alert alert-warning">
                        No se pudo cargar la información financiera
                      </div>
                    )}
                  </div>
                </div>

                {/* Historial con pestañas */}
                <div className="card">
                  <div className="card-header bg-secondary text-white p-0">
                    <ul className="nav nav-tabs border-0 mb-0">
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeHistoryTab === 'payments' ? 'active text-white bg-secondary' : 'text-white-50'} border-0`}
                          onClick={() => setActiveHistoryTab('payments')}
                        >
                          <i className="bi bi-cash-stack me-2"></i>
                          Historial de Pagos
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeHistoryTab === 'activity' ? 'active text-white bg-secondary' : 'text-white-50'} border-0`}
                          onClick={() => setActiveHistoryTab('activity')}
                        >
                          <i className="bi bi-clock-history me-2"></i>
                          Trazabilidad Completa
                        </button>
                      </li>
                    </ul>
                  </div>
                  <div className="card-body p-0">
                    {activeHistoryTab === 'payments' ? (
                      // Historial de Pagos
                      <>
                        {paymentHistory.length > 0 && (
                          <div className="p-2 bg-light border-bottom d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              <i className="bi bi-info-circle me-1"></i>
                              {paymentHistory.length} pago{paymentHistory.length > 1 ? 's' : ''} registrado{paymentHistory.length > 1 ? 's' : ''}
                            </small>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={handleUndoLastPayment}
                              disabled={loading}
                              title="Eliminar el último pago registrado"
                            >
                              <i className="bi bi-arrow-counterclockwise me-1"></i>
                              Deshacer último pago
                            </button>
                          </div>
                        )}
                        {paymentHistory.length > 0 ? (
                      <div className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {paymentHistory.map((payment, index) => (
                          <div key={payment.id} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <div className="fw-bold">
                                  Pago #{paymentHistory.length - index}
                                  {index === 0 && (
                                    <span className="badge bg-primary ms-2">Más reciente</span>
                                  )}
                                </div>
                                <small className="text-muted">
                                  {formatDate(payment.activity_date)} - {payment.activity_by_name}
                                </small>
                                
                                {payment.payment_amount > 0 && (
                                  <div className="mt-1">
                                    <small>Cliente pagó: </small>
                                    <strong className="text-success">{formatCurrency(payment.payment_amount)}</strong>
                                  </div>
                                )}
                                
                                {payment.credited_amount > 0 && (
                                  <div>
                                    <small>Acreditado: </small>
                                    <strong className="text-info">{formatCurrency(payment.credited_amount)}</strong>
                                  </div>
                                )}
                                
                                {payment.activity_description && (
                                  <div className="mt-1">
                                    <small className="text-muted fst-italic">{payment.activity_description}</small>
                                  </div>
                                )}
                              </div>
                              
                              <div className="text-end">
                                <div className="badge bg-secondary">
                                  Total: {formatCurrency(payment.total_balance)}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                        ) : (
                          <div className="p-3 text-center text-muted">
                            <i className="bi bi-inbox display-6"></i>
                            <p className="mt-2 mb-0">Sin pagos registrados</p>
                          </div>
                        )}
                      </>
                    ) : (
                      // Historial de Actividad Completo
                      activityHistory.length > 0 ? (
                        <div className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {activityHistory.map((activity, index) => (
                            <div key={activity.id} className="list-group-item">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-1">
                                    <span className="badge bg-primary me-2">#{activityHistory.length - index}</span>
                                    <strong>{activity.status_description || 'Cambio de estado'}</strong>
                                  </div>
                                  <small className="text-muted">
                                    {formatDate(activity.activity_date)} - {activity.activity_by_name}
                                  </small>
                                  
                                  {activity.payment_amount > 0 && (
                                    <div className="mt-1">
                                      <span className="badge bg-success me-1">
                                        <i className="bi bi-cash me-1"></i>
                                        Pago cliente: {formatCurrency(activity.payment_amount)}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {activity.credited_amount > 0 && (
                                    <div className="mt-1">
                                      <span className="badge bg-info me-1">
                                        <i className="bi bi-bank me-1"></i>
                                        Acreditado: {formatCurrency(activity.credited_amount)}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {activity.controlled && (
                                    <div className="mt-1">
                                      <span className="badge bg-warning text-dark">
                                        <i className="bi bi-shield-check me-1"></i>
                                        Controlado
                                      </span>
                                    </div>
                                  )}
                                  
                                  {activity.activity_description && (
                                    <div className="mt-2 p-2 bg-light rounded">
                                      <small className="text-muted">{activity.activity_description}</small>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-3 text-center text-muted">
                          <i className="bi bi-inbox display-6"></i>
                          <p className="mt-2 mb-0">Sin actividad registrada</p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* COLUMNA DERECHA: Cambio de Estado */}
              <div className="col-md-7">
                <form onSubmit={handleChangeStatus}>
                  <div className="card">
                    <div className="card-header bg-warning">
                      <h6 className="mb-0">
                        <i className="bi bi-gear me-2"></i>
                        Cambiar Estado del Pedido
                      </h6>
                    </div>
                    <div className="card-body">
                      {/* Estado Actual */}
                      <div className="alert alert-info">
                        <strong>Estado Actual:</strong> {order?.status_description || 'Desconocido'}
                      </div>

                      {/* Selector de Nuevo Estado */}
                      <div className="mb-3">
                            <label className="form-label fw-bold">Nuevo Estado *</label>
                            <select
                              className="form-select"
                              value={selectedStatus}
                              onChange={(e) => setSelectedStatus(e.target.value)}
                              required
                            >
                              <option value="">Seleccione un estado...</option>
                              {statuses.map(status => (
                                <option key={status.id} value={status.id}>
                                  {status.status_description}
                                  {status.require_control === 1 && ' 🔒'}
                                  {status.payment_requirement_type !== 'NONE' && ' 💰'}
                                </option>
                              ))}
                            </select>
                            <small className="text-muted">
                              🔒 = Requiere control | 💰 = Requiere validación de pago
                            </small>
                          </div>

                      {/* Validación de Pago */}
                      {requiresPaymentValidation && validation && (
                        <div className={`alert ${validation.allowed ? 'alert-success' : 'alert-danger'} d-flex align-items-center`}>
                          <i className={`bi ${validation.allowed ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-2 fs-5`}></i>
                          <div className="flex-grow-1">
                            <strong>{validation.allowed ? '✓ Validación exitosa' : '✗ Validación fallida'}</strong>
                            <p className="mb-0">{validation.message}</p>
                            
                            {validation.details && selectedStatusInfo && (
                              <div className="mt-2">
                                <small>
                                  {selectedStatusInfo.payment_requirement_type === 'PARTIAL' && (
                                    <>Requiere al menos un pago registrado</>
                                  )}
                                  {selectedStatusInfo.payment_requirement_type === 'FULL' && (
                                    <>Requiere pago completo según forma de pago</>
                                  )}
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Control Financiero */}
                      {selectedStatusInfo?.require_control === 1 && (
                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="controlled"
                              checked={controlled}
                              onChange={(e) => setControlled(e.target.checked)}
                            />
                            <label className="form-check-label fw-bold" htmlFor="controlled">
                              Marcar como controlado por finanzas
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Observaciones */}
                      <div className="mb-3">
                        <label className="form-label">Observaciones</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={activityDescription}
                          onChange={(e) => setActivityDescription(e.target.value)}
                          placeholder="Detalles del cambio de estado..."
                        />
                      </div>

                          {/* Información del Estado Seleccionado */}
                          {selectedStatusInfo && (
                            <div className="alert alert-light border">
                              <h6 className="mb-2">Información del Estado:</h6>
                              <ul className="mb-0">
                                {selectedStatusInfo.require_control === 1 && (
                                  <li>Requiere control de finanzas</li>
                                )}
                                {selectedStatusInfo.payment_requirement_type === 'PARTIAL' && (
                                  <li>Requiere al menos un pago parcial registrado</li>
                                )}
                                {selectedStatusInfo.payment_requirement_type === 'FULL' && (
                                  <li>Requiere pago completo según forma de pago</li>
                                )}
                                {selectedStatusInfo.payment_requirement_type === 'NONE' && (
                                  <li>No requiere validación de pago</li>
                                )}
                              </ul>
                            </div>
                          )}
                    </div>
                  </div>

                  {/* Botones */}
                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClose}
                      disabled={loading}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary flex-grow-1"
                      disabled={loading || !selectedStatus || (validation && !validation.allowed)}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Cambiando estado...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Confirmar Cambio de Estado
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmación para Eliminar Pago */}
      {showDeleteConfirmModal && paymentToDelete && (
        <div 
          className="modal fade show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060 }}
          onClick={() => setShowDeleteConfirmModal(false)}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg">
              {/* Header con gradiente rojo */}
              <div className="modal-header bg-danger text-white border-0">
                <h5 className="modal-title d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill me-2 fs-4"></i>
                  Confirmar Eliminación de Pago
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowDeleteConfirmModal(false)}
                  disabled={loading}
                ></button>
              </div>

              {/* Body con información del pago */}
              <div className="modal-body p-4">
                <div className="alert alert-warning border-0 bg-warning bg-opacity-10 mb-4">
                  <div className="d-flex align-items-start">
                    <i className="bi bi-info-circle-fill text-warning me-3 fs-4 mt-1"></i>
                    <div>
                      <strong className="d-block mb-1">¡Atención!</strong>
                      <small>Esta acción no se puede deshacer. El pago será eliminado permanentemente del sistema.</small>
                    </div>
                  </div>
                </div>

                <div className="card bg-light border-0 mb-3">
                  <div className="card-body">
                    <h6 className="card-subtitle mb-3 text-muted">
                      <i className="bi bi-receipt me-2"></i>
                      Detalles del Pago a Eliminar:
                    </h6>
                    
                    <div className="row g-3">
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <span className="text-muted">
                            <i className="bi bi-calendar3 me-2"></i>
                            Fecha:
                          </span>
                          <strong>{formatDate(paymentToDelete.activity_date)}</strong>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <span className="text-muted">
                            <i className="bi bi-person me-2"></i>
                            Registrado por:
                          </span>
                          <strong>{paymentToDelete.activity_by_name}</strong>
                        </div>
                      </div>

                      {paymentToDelete.payment_amount > 0 && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <span className="text-muted">
                              <i className="bi bi-cash-stack me-2"></i>
                              Pagado por cliente:
                            </span>
                            <strong className="text-success">{formatCurrency(paymentToDelete.payment_amount)}</strong>
                          </div>
                        </div>
                      )}

                      {paymentToDelete.credited_amount > 0 && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <span className="text-muted">
                              <i className="bi bi-bank me-2"></i>
                              Acreditado en caja:
                            </span>
                            <strong className="text-info">{formatCurrency(paymentToDelete.credited_amount)}</strong>
                          </div>
                        </div>
                      )}

                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-2">
                          <span className="text-muted">
                            <i className="bi bi-wallet2 me-2"></i>
                            Total acumulado:
                          </span>
                          <strong className="text-primary">{formatCurrency(paymentToDelete.total_balance)}</strong>
                        </div>
                      </div>

                      {paymentToDelete.activity_description && (
                        <div className="col-12">
                          <div className="pt-2 border-top">
                            <small className="text-muted d-block mb-1">
                              <i className="bi bi-chat-left-text me-2"></i>
                              Observaciones:
                            </small>
                            <p className="mb-0 fst-italic">{paymentToDelete.activity_description}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-center text-muted">
                  <small>
                    <i className="bi bi-question-circle me-1"></i>
                    ¿Está seguro de que desea eliminar este pago?
                  </small>
                </div>
              </div>

              {/* Footer con botones */}
              <div className="modal-footer border-0 bg-light">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirmModal(false)}
                  disabled={loading}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDeletePayment}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash3 me-2"></i>
                      Sí, Eliminar Pago
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatusChangeModal;
